import { useContext, useEffect, useState } from "react";
import { CoreFieldProps } from "../Fields/types/fieldTypes";
import { SkeletonComponent } from "@syncfusion/ej2-react-notifications";
import systemFieldConfig, { FieldConfig } from "./FieldConfig";
import { FormFieldSettingsContext, FormFieldPropsContext } from "./FieldFormV2";
import classNames from "classnames";
import { FormFieldConfigContext } from "./FormFieldConfigContext";
import { isNil } from "lodash";

export interface FieldV2Props<T = unknown> {
  fieldProps: string | Partial<CoreFieldProps>;
  handleOnBlur?: any;
  customFieldConfig?: FieldConfig;
  complexFieldValue?: T;
  onReady?: () => void;
}

export function FieldV2Component<T = unknown>(props: FieldV2Props<T>) {
  const formFieldConfigContextValue = useContext(FormFieldConfigContext);
  const formFieldSettings = useContext(FormFieldSettingsContext);
  const formFieldsData = useContext(FormFieldPropsContext);

  const [ready, setReady] = useState(false);
  const [fieldProps, setFieldProps] = useState<Partial<CoreFieldProps>>();

  useEffect(() => {
    updateFieldProps();
  }, [formFieldSettings, formFieldsData, props.fieldProps]);

  useEffect(() => {
    setupFields();
  }, []);

  const updateFieldProps = () => {
    // Update fieldprops
    if (props.fieldProps === undefined) {
      throw new Error("FieldProps is required, but not provided");
    }

    if (formFieldsData && typeof props.fieldProps === "string") {
      const fieldProps = formFieldsData.find(
        (field) => field.fieldKey === props.fieldProps
      );
      if (fieldProps) {
        let modFieldProps = {
          ...fieldProps,
          ...{
            disabled:
              formFieldSettings.areAllFieldsDisabled || fieldProps.disabled,
            hidden:
              (formFieldSettings.availableFields || []).length > 0
                ? !formFieldSettings.availableFields?.includes(
                    fieldProps.fieldKey || ""
                  ) || fieldProps.hidden
                : fieldProps.hidden,
          },
        };

        if (props.complexFieldValue) {
          modFieldProps.complexFieldValue = props.complexFieldValue;
        }

        modFieldProps.errorMessages = !isNil(formFieldConfigContextValue?.errorMessagesCount) ? (modFieldProps.errorMessages || []).slice(0, formFieldConfigContextValue.errorMessagesCount) 
        : (modFieldProps.errorMessages || []);

        setFieldProps(modFieldProps);
      }
    } else if (
      formFieldsData &&
      typeof props.fieldProps !== "string" &&
      props.fieldProps.fieldKey
    ) {
      const fieldProps = formFieldsData.find(
        (field) =>
          field.fieldKey ===
          (props.fieldProps as Partial<CoreFieldProps>).fieldKey
      );

      // If there were `fieldProps` from the form already, i.e. from the schema, then merge
      if (fieldProps) {
        let modFieldProps = {
          ...fieldProps,
          ...props.fieldProps,
          ...{
            disabled:
              // The idea is that we want to look for all places where things may be disabled, and if any of them are disabled, then the field should be disabled.
              formFieldSettings.areAllFieldsDisabled ||
              fieldProps.disabled ||
              props.fieldProps.disabled ||
              false,
          },
        };

        modFieldProps.hidden =
          (formFieldSettings.availableFields || []).length > 0
            ? !formFieldSettings.availableFields?.includes(
                modFieldProps.fieldKey || ""
              ) || modFieldProps.hidden
            : modFieldProps.hidden;

        modFieldProps.errorMessages = !isNil(formFieldConfigContextValue?.errorMessagesCount) ? (modFieldProps.errorMessages || []).slice(0, formFieldConfigContextValue.errorMessagesCount) 
        : (modFieldProps.errorMessages || []);

        setFieldProps(modFieldProps);
      } else {
        let modFieldProps = {
          ...props.fieldProps,
          ...{
            disabled:
              formFieldSettings.areAllFieldsDisabled ||
              props.fieldProps.disabled,
          },
        };

        modFieldProps.hidden =
          (formFieldSettings.availableFields || []).length > 0
            ? !formFieldSettings.availableFields?.includes(
                modFieldProps.fieldKey || ""
              ) || modFieldProps.hidden
            : modFieldProps.hidden;

        modFieldProps.errorMessages = !isNil(formFieldConfigContextValue?.errorMessagesCount) ? (modFieldProps.errorMessages || []).slice(0, formFieldConfigContextValue.errorMessagesCount) 
        : (modFieldProps.errorMessages || []);

        setFieldProps(modFieldProps);
      }
    }
  };

  const setupFields = () => {
    setReady(true);
  };

  useEffect(() => {
    if (ready) {
      props.onReady && props.onReady();
    }
  }, [props.onReady]);

  return (
    <>
      {!ready && <SkeletonComponent height="15px"></SkeletonComponent>}
      {ready && fieldProps && fieldProps.hidden && <></>}
      {ready && fieldProps && !fieldProps.hidden && (
        <div
          className={classNames(
            "base-field",
            "ucl-base-field",
            fieldProps.className ? ` base-field-${fieldProps.className}` : "",
            fieldProps.type_AsString
              ? ` base-field-${fieldProps.type_AsString}`
              : "",
            fieldProps.fieldKey ? ` base-field-${fieldProps.fieldKey}` : "",
            fieldProps.visible === false ? ` ucl-base-field-not-visible` : ""
          )}
          onBlur={props.handleOnBlur}
        >
          {(function () {
            if (!fieldProps.type) {
              console.error(
                `Field type is required to render a field, but fieldProps.type is null`,
                fieldProps
              );
              return <></>;
            }
            const fieldConfig =
              formFieldConfigContextValue?.fieldConfig ||
              props.customFieldConfig ||
              systemFieldConfig;
            const FieldComponent = fieldConfig[fieldProps.type] as React.FC<
              Partial<CoreFieldProps>
            >;

            if (!FieldComponent) {
              console.error(
                `Field type ${fieldProps.type} is not defined in FieldConfig`
              );

              return <></>;
            }

            return <FieldComponent {...fieldProps} />;
          })()}
        </div>
      )}
    </>
  );
}

export const FieldV2 = FieldV2Component;
export default FieldV2;
